import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateShowsHostedConfig } from "../../services/rewards.service";
import { parseInteger, getFieldErrorMessage } from "../../utils/rewardsConfig";

const ShowsHostedBlock = ({ config, onSuccess }) => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(config?.enabled ?? false);
  const [minShowsPerWeek, setMinShowsPerWeek] = useState(
    config?.minShowsPerWeek !== null ? String(config.minShowsPerWeek) : "",
  );
  const [minDurationSeconds, setMinDurationSeconds] = useState(
    config?.minDurationSeconds !== null
      ? String(config.minDurationSeconds)
      : "",
  );
  const [amountTokens, setAmountTokens] = useState(
    config?.amountTokens !== null ? String(config.amountTokens) : "",
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [genericError, setGenericError] = useState("");

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled ?? false);
      setMinShowsPerWeek(
        config.minShowsPerWeek !== null ? String(config.minShowsPerWeek) : "",
      );
      setMinDurationSeconds(
        config.minDurationSeconds !== null
          ? String(config.minDurationSeconds)
          : "",
      );
      setAmountTokens(
        config.amountTokens !== null ? String(config.amountTokens) : "",
      );
    }
  }, [config]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: (body) => updateShowsHostedConfig(body),
    onSuccess: (data) => {
      setFieldErrors({});
      setGenericError("");
      queryClient.setQueryData(["rewards-config"], (prev) =>
        prev ? { ...prev, showsHosted: data } : { showsHosted: data },
      );
      queryClient.invalidateQueries({ queryKey: ["rewards"] });
      onSuccess?.();
      alert("Shows hosted config saved");
    },
    onError: (err) => {
      setFieldErrors(err.errors ?? {});
      setGenericError(err.message || "Failed to save");
    },
  });

  const handleSave = () => {
    setGenericError("");
    const minShows = parseInteger(minShowsPerWeek, 0);
    const minDur = parseInteger(minDurationSeconds, 0);
    const amountTokensNum = parseInteger(amountTokens, 0);
    const errors = {};
    if (minShows < 1) {
      errors.minShowsPerWeek = "Must be at least 1";
    }
    if (minDur < 60) {
      errors.minDurationSeconds = "Must be at least 60 seconds";
    }
    if (amountTokensNum < 1) {
      errors.amountTokens = "Must be at least 1";
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    save({
      enabled,
      minShowsPerWeek: minShows,
      minDurationSeconds: minDur,
      amountTokens: amountTokensNum,
    });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Shows hosted
      </Typography>
      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setGenericError("");
                handleSave();
              }}
            >
              Retry
            </Button>
          }
        >
          {genericError}
        </Alert>
      )}
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          }
          label="Enabled"
        />
        <TextField
          label="Min shows per week"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={minShowsPerWeek}
          onChange={(e) => setMinShowsPerWeek(e.target.value)}
          error={!!fieldErrors.minShowsPerWeek}
          helperText={getFieldErrorMessage(fieldErrors, "minShowsPerWeek")}
        />
        <TextField
          label="Min duration (seconds)"
          type="number"
          inputProps={{ min: 60, step: 1 }}
          value={minDurationSeconds}
          onChange={(e) => setMinDurationSeconds(e.target.value)}
          error={!!fieldErrors.minDurationSeconds}
          helperText={getFieldErrorMessage(fieldErrors, "minDurationSeconds")}
        />
        <TextField
          label="Amount (tokens)"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={amountTokens}
          onChange={(e) => setAmountTokens(e.target.value)}
          error={!!fieldErrors.amountTokens}
          helperText={getFieldErrorMessage(fieldErrors, "amountTokens")}
        />
        <Button variant="contained" onClick={handleSave} disabled={isPending}>
          {isPending ? "Saving…" : "Save"}
        </Button>
      </Box>
    </Paper>
  );
};

export default ShowsHostedBlock;
